'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var client = require('@apollo/client');
var retry = require('@apollo/client/link/retry');
var batchHttp = require('@apollo/client/link/batch-http');
var graphqlTag = require('graphql-tag');

const MAX_RETRY_ATTEMPTS = 5;
const MAX_BATCH = 10; // 10 is what Apollo uses for the default

// The amount of time to wait to invalidate the cache.
const CACHE_TTL_MINS = 30;

// How fast to check if the cache is due for invalidation.
const CACHE_TTL_CHECK_MS = 1000;
const query = graphqlTag.gql`
  {
    cacheTtl @client
  }
`;
var apolloClientFactory = ({
  name,
  connectToDevTools,
  uri,
  maxRetryAttempts = MAX_RETRY_ATTEMPTS,
  maxBatch = MAX_BATCH,
  batchInterval,
  cache: cacheParam,
  cacheTtlMins = CACHE_TTL_MINS,
  cacheTtlCheckMs = CACHE_TTL_CHECK_MS,
  restLinks = {},
  fetch,
  ssrMode = false,
  link = ({
    rest,
    retry,
    batch
  }) => client.from([rest, retry, batch]),
  initialState,
  typePolicies,
  addTypename,
  possibleTypes,
  persistor = null
} = {}) => {
  let cache;
  if (cacheParam) {
    cache = cacheParam;
  } else {
    cache = new client.InMemoryCache({
      addTypename,
      possibleTypes,
      typePolicies
    });
    if (initialState) {
      cache = cache.restore(initialState);
    }
  }
  const httpLink = maxBatch > 1 ? new batchHttp.BatchHttpLink({
    uri,
    fetch,
    batchMax: maxBatch,
    batchInterval
  }) : new client.HttpLink({
    uri,
    fetch,
    useGETForQueries: true
  });
  const client$1 = new client.ApolloClient({
    name,
    connectToDevTools,
    queryDeduplication: true,
    cache,
    link: link({
      retry: new retry.RetryLink({
        attempts: {
          max: maxRetryAttempts
        }
      }),
      // the key "batch" is part of an existing interface, so do not change it
      // however, it is safe to place either an HttpLink or HttpBatchLink here
      batch: httpLink,
      rest: new client.ApolloLink((operation, forward) => {
        const restLink = restLinks[operation.operationName];
        if (restLink) return restLink.request(operation, forward);
        if (forward) return forward(operation);
        return null;
      })
    }),
    ssrMode
  });
  const createCacheTtl = () => {
    const now = new Date();
    now.setTime(now.getTime() + cacheTtlMins * 60 * 1000);
    client$1.writeQuery({
      query,
      data: {
        cacheTtl: now.toISOString()
      }
    });
  };
  const checkCacheTtl = async () => {
    const {
      cacheTtl
    } = (() => {
      try {
        return client$1.readQuery({
          query
        }) || {};
      } catch (err) {
        return {};
      }
    })();
    if (typeof cacheTtl !== 'string' || Number.isNaN(new Date(cacheTtl).getTime())) {
      createCacheTtl();
      return;
    }
    if (new Date(cacheTtl).getTime() - new Date().getTime() <= 0) {
      let CacheClearStatus = /*#__PURE__*/function (CacheClearStatus) {
        CacheClearStatus[CacheClearStatus["CACHE_CLEARED"] = 0] = "CACHE_CLEARED";
        return CacheClearStatus;
      }({});
      const tryToClearCache = async () => {
        await client$1.resetStore();
        if (persistor) {
          await persistor.purge();
        }
        createCacheTtl();
        return CacheClearStatus.CACHE_CLEARED;
      };
      if ((await tryToClearCache()) === CacheClearStatus.CACHE_CLEARED) return;

      // TODO: fix this instead of disabling
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      const interval = setInterval(async () => {
        if ((await tryToClearCache()) === CacheClearStatus.CACHE_CLEARED) {
          clearInterval(interval);
        }
      }, 200);
    }
  };
  let cacheTtlTimeout = setTimeout(() => undefined, 0);
  const startCacheTtlChecker = () => {
    // TODO: fix this instead of disabling
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    cacheTtlTimeout = setTimeout(async () => {
      await checkCacheTtl();
      startCacheTtlChecker();
    }, cacheTtlCheckMs);
  };
  const stopCacheTtlChecker = () => {
    clearTimeout(cacheTtlTimeout);
  };
  return {
    client: client$1,
    checkCacheTtl,
    startCacheTtlChecker,
    stopCacheTtlChecker,
    persistor
  };
};

exports.CACHE_TTL_CHECK_MS = CACHE_TTL_CHECK_MS;
exports.CACHE_TTL_MINS = CACHE_TTL_MINS;
exports["default"] = apolloClientFactory;
